import { useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * The first name in a display name: "Robert Stacey" and "Stacey, Robert"
 * both give "Robert". Guests and empty names give undefined.
 */
export function firstName(displayName?: string): string | undefined {
  if (!displayName) return undefined;
  const [before, after] = displayName.split(',');
  const first = (after ?? before).trim().split(/\s+/)[0];
  return first && first.toLowerCase() !== 'guest' ? first : undefined;
}

/**
 * The signed-in person's first name for a greeting: the sign-in profile first,
 * then the person's catalog User entity (guest sign-in has no profile).
 * Undefined while loading, on any error, or when no name is known, so the
 * greeting falls back to a plain welcome.
 */
export function useFirstName(): string | undefined {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [name, setName] = useState<string>();
  useEffect(() => {
    let active = true;
    (async () => {
      const profile = await identityApi.getProfileInfo();
      let displayName = profile.displayName;
      if (!displayName) {
        const { userEntityRef } = await identityApi.getBackstageIdentity();
        const user = await catalogApi.getEntityByRef(userEntityRef);
        displayName = (
          user?.spec?.profile as { displayName?: string } | undefined
        )?.displayName;
      }
      if (active) setName(firstName(displayName));
    })().catch(() => {
      /* A missing name only means a plain welcome. */
    });
    return () => {
      active = false;
    };
  }, [identityApi, catalogApi]);
  return name;
}
